"use client"

import Link from "next/link"
import { useRouter } from "next/navigation"
import { useEffect, type ReactNode } from "react"
import { BookOpen } from "lucide-react"

import { useArticleEntitlement } from "@/components/marketing/use-article-entitlement"
import {
  FREE_ARTICLE_LIMIT,
  METER_WARN_REMAINING,
  useArticleMeter,
} from "@/lib/articles/meter"

// The article subscription wall. The first FREE_ARTICLE_LIMIT articles each
// calendar month are open to anyone; after that the subscription
// ($9.99/month or $99/year - web/lib/billing/plans.ts) keeps the library
// open, and the $49 community membership reads with no cap.
//
// This component is UX: the ENFORCEMENT is the server-side meter
// (web/proxy.ts and lib/articles/meter-cookie.ts), which sends an over-limit
// reader to /articles/subscribe for every request for an unopened article, an
// in-site click's included, so the text never reaches him. This wall covers
// what reaches the page anyway: a burst of tabs opened at once (the local
// mirror catches what the server could not count in time), and a page the
// router fetched ahead of a click before the allowance ran out. When the
// server itself rules the article walled, the wall sends him on to the
// subscribe page, as the proxy would have.
//
// Two properties are load-bearing and must survive any edit here:
//
//  1. The article body is ALWAYS in the server-rendered HTML. This component
//     only layers over it in the browser. That is what keeps Googlebot and
//     every AI crawler reading the full text, and it is why the JSON-LD on the
//     article route carries the paywalled-content markup instead of pretending
//     nothing is gated.
//  2. Nothing here inspects the user agent. Serving different content to a
//     crawler than to a reader is cloaking, and it is the one thing that would
//     actually cost the library its rankings.
//
// AND THE WALL ASKS ABOUT ENTITLEMENT, NEVER ABOUT BEING SIGNED IN. It used to
// stand down for any signed-in reader, and anyone can make a free account, so
// "signed in" handed a free account every page the local mirror walls.
// `useArticleEntitlement` asks the server the question that actually decides
// it, and a signed-out reader answers it locally with no request at all.

// Articles this page has already sent on to the subscribe page. The subscribe
// page bounces an entitled reader straight back, so a second ruling for the
// same article in this tab never sends him round again.
const sentToSubscribe = new Set<string>()

export function ArticleGate({
  slug,
  children,
}: {
  slug: string
  children: ReactNode
}) {
  const router = useRouter()
  const { unlimited, ready: entitlementReady } = useArticleEntitlement()
  const { gated, ready, walledByServer } = useArticleMeter(slug)

  // The server's ruling already weighed entitlement, so it needs no second
  // answer before sending him where every other over-limit request goes.
  useEffect(() => {
    if (!walledByServer || sentToSubscribe.has(slug)) return
    sentToSubscribe.add(slug)
    router.replace(`/articles/subscribe?article=${encodeURIComponent(slug)}`)
  }, [walledByServer, router, slug])

  // Both `ready` flags are false on the server and the first client render, so
  // server and client agree on the first paint with no separate hydration
  // flag, and a subscriber never sees the wall flash while his entitlement is
  // still in flight. A verified metered answer is the only one that can show
  // the wall; unknown leaves the already-delivered body alone without granting
  // new server access.
  const showWall = ready && entitlementReady && gated && unlimited === false

  if (!showWall) return <>{children}</>

  return (
    <div className="relative">
      <div
        aria-hidden
        className="pointer-events-none max-h-[34rem] select-none overflow-hidden blur-[6px] [mask-image:linear-gradient(to_bottom,black,transparent)]"
      >
        {children}
      </div>
      <div className="absolute inset-0 flex items-start justify-center px-4 pt-16 sm:pt-24">
        <div className="w-full max-w-[30rem] rounded-[var(--radius-xl)] border border-[var(--color-rule)] bg-[var(--color-paper)] p-7 text-center shadow-[var(--glass-shadow-2)] sm:p-9">
          <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full border border-[var(--color-rule-strong)] text-[var(--color-vermilion)]">
            <BookOpen size={18} aria-hidden />
          </span>
          <h2 className="mt-4 text-[1.7rem] leading-tight text-[var(--color-ink)]">
            Keep reading
          </h2>
          <p className="mt-2 text-[0.96rem] leading-[1.55] text-[var(--color-ink-muted)]">
            You have read your {FREE_ARTICLE_LIMIT} free articles this month.
            Subscribe and the whole library opens, with no monthly cap.
            Articles you already opened stay open.
          </p>
          <Link
            href={`/articles/subscribe?article=${encodeURIComponent(slug)}`}
            className="mt-5 inline-flex min-h-12 w-full items-center justify-center rounded-[10px] bg-[var(--color-vermilion)] px-5 text-[0.98rem] font-extrabold text-white transition-colors hover:bg-[var(--color-oxblood)]"
          >
            See the two plans
          </Link>
          <p className="mt-2.5 text-[0.8rem] text-[var(--color-ink-soft)]">
            $9.99 a month or $99 a year. Community members read free.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * The quiet count that appears as a reader nears the limit. Renders nothing
 * until he is close, nothing once he is walled (the wall speaks for itself),
 * and nothing for an unlimited or unknown entitlement. A signed-in reader
 * verified as metered still sees it, because telling him so late is how the
 * wall arrives as a surprise.
 */
export function ArticleMeterNotice({ slug }: { slug: string }) {
  const { unlimited, ready: entitlementReady } = useArticleEntitlement()
  const { remaining, gated, ready } = useArticleMeter(slug)

  if (!ready || !entitlementReady || unlimited !== false || gated) return null
  if (remaining > METER_WARN_REMAINING || remaining < 1) return null

  return (
    <p className="mt-6 text-center text-[0.85rem] text-[var(--color-ink-soft)]">
      {remaining === 1
        ? "One more free article this month. A subscription keeps the library open."
        : `${remaining} more free articles this month. A subscription keeps the library open.`}
    </p>
  )
}
